import escapeHtml from 'lodash.escape';
import PropTypes from 'prop-types';
import React from 'react';
import { CSVLink } from 'react-csv';

import { useGetLocalization } from '../../../../hooks';
import { ButtonSubmit } from '../../../reusable';

function BankSummaryTable({ bankSummaries }) {
  const getLocalization = useGetLocalization();
  const getCurrencySymbol = (currencyCode) => {
    return (0)
      .toLocaleString(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .replace(/\d/g, '')
      .trim();
  };

  const sortBankSummariesBySubTenderTypes = () => {
    const sortedSummaries = {};

    Object.values(bankSummaries).forEach((summary) => {
      const currency = summary.subTenderType || summary.currencyCode;
      if (!sortedSummaries[currency]) {
        sortedSummaries[currency] = [];
      }
      sortedSummaries[currency].push(summary);
    });

    return sortedSummaries;
  };

  const convertSortedSummariesToArray = (sortedSummaries) => {
    const resultArray = [];

    Object.entries(sortedSummaries).forEach(([currency, summaries]) => {
      resultArray.push({ localeDate: `${getCurrencySymbol(currency)} ${currency}` });
      resultArray.push(...summaries);
    });

    return resultArray;
  };

  const sortedSummaries = sortBankSummariesBySubTenderTypes();
  const sortedBankSummaryArray = convertSortedSummariesToArray(sortedSummaries);

  const COLUMNS = [
    { key: 'localeDate', label: getLocalization('lbl.date-time') },
    { key: 'depositSlip', label: getLocalization('lbl.deposit-number') },
    { key: 'localeDepositAmount', label: getLocalization('lbl.deposited'), textAlign: 'right' },
    {
      key: 'localeDepositAmountBeforeDeposit',
      label: getLocalization('lbl.expected'),
      textAlign: 'right',
    },
    { key: 'localeVariance', label: getLocalization('lbl.variance'), textAlign: 'right' },
    { key: 'varianceReason', label: getLocalization('lbl.reason') },
    { key: 'varianceExplanation', label: getLocalization('lbl.variance-explanation') },
    { key: 'athleteInfo', label: getLocalization('lbl.athlete') },
  ];

  const headerCell = (fieldName, idx, value, textAlign = 'center') => (
    <th
      key={`BankSummaryTable_th_${fieldName}_${idx}`}
      style={{
        background: 'black',
        color: 'white',
        fontSize: '20px',
        textAlign,
      }}
    >
      {escapeHtml(value)}
    </th>
  );

  const dataCell = (fieldName, idx, value, textAlign = 'center', color = '#111') => (
    <td
      key={`BankSummaryTable_td_${fieldName}_${idx}`}
      style={{
        background: '#FAFAFA',
        border: '1px solid #E5E5E5',
        color,
        textAlign,
      }}
    >
      {escapeHtml(value)}
    </td>
  );

  return (
    <>
      <h1 className="text-color-accent">{getLocalization('lbl.search-results')}</h1>
      <table
        style={{ border: '1px solid #E5E5E5', borderCollapse: 'collapse', width: '100%' }}
        dir="ltr"
      >
        <thead>
          <tr key="BankSummaryTable_Header_0">
            {COLUMNS.map(({ key, label, textAlign }, columnIdx) =>
              headerCell(key, columnIdx, label, textAlign),
            )}
          </tr>
        </thead>
        {Object.keys(sortedSummaries).map((key) => (
          <React.Fragment key={key}>
            <tr>
              <td
                colSpan={COLUMNS.length}
                style={{ textAlign: 'left', fontWeight: 'bold', paddingLeft: '5px' }}
              >
                {`${getCurrencySymbol(key)} ${key}`}
              </td>
            </tr>
            <tbody>
              {sortBankSummariesBySubTenderTypes()[key].map((summary, summaryIdx) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={`BankSummaryTable_Data_${key}_${summaryIdx}`}>
                  {COLUMNS.map(({ key: columnKey, textAlign }, columnIdx) =>
                    dataCell(columnKey, columnIdx, summary[columnKey], textAlign),
                  )}
                </tr>
              ))}
            </tbody>
          </React.Fragment>
        ))}
      </table>
      <article className="va-sm-t ta-sm-c ncss-col-sm-12 ncss-col-md-6 ncss-col-lg-4 p4-sm">
        <CSVLink
          key="CsvLink"
          data={sortedBankSummaryArray}
          filename="bank-summary.csv"
          headers={COLUMNS}
          target="_blank"
        >
          <ButtonSubmit
            key="CsvExportButton"
            label={getLocalization('lbl.export')}
            onSubmit={() => {}}
          />
        </CSVLink>
      </article>
    </>
  );
}

BankSummaryTable.propTypes = {
  bankSummaries: PropTypes.arrayOf(
    PropTypes.shape({
      athleteInfo: PropTypes.string.isRequired,
      depositSlip: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      localeDate: PropTypes.string.isRequired,
      localeDepositAmount: PropTypes.string.isRequired,
      localeDepositAmountBeforeDeposit: PropTypes.string.isRequired,
      localeVariance: PropTypes.string.isRequired,
      varianceReason: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default BankSummaryTable;
